import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { digestDateLabel, type NewsDay as Day, type NewsItem } from '../../utils/newsDigest';

/**
 * One day of the round-up.
 *
 * The layout, and why it is this and not something else:
 *
 * - **Headlines run the full width.** The container is 1180px; earlier passes
 *   capped headlines at 22-38 characters, which on a normal window pushed
 *   everything into the left half and left a dead column of black. A headline
 *   at this size wants roughly 38 characters a line, which IS the full
 *   measure here. Nothing gets an arbitrary ch cap any more.
 * - **Summary and Portal's take sit side by side.** That uses the width
 *   honestly rather than padding it out, keeps the summary near 60 characters
 *   where it reads well, and separates Portal's opinion from the reporting
 *   structurally — a column apart, not a rule and some margin.
 * - **No contents list.** There was one for a version, to fix a real problem:
 *   the day used to carry its own headline, written separately, which turned
 *   out to be a reworded copy of one of the stories — so the top headline's
 *   article was the second one down. That headline is gone, and with two to
 *   five stories a list that repeats every headline immediately above the
 *   stories themselves is just saying everything twice. The count in the date
 *   line gives the shape of the day without the repetition.
 *
 * Numbers stay on the stories so a day has a shape and "number two" means
 * something in conversation.
 */

/** "10 Nov 2026" — never a raw ISO date in front of a reader. */
function shortDate(iso: string): string {
  if (!iso) return '';
  const parsed = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

/** Opportunities first — they are the half with a deadline attached. */
function ordered(items: NewsItem[]): NewsItem[] {
  const weight = (item: NewsItem) => item.category === 'opportunity' ? 0 : 1;
  return [...items].sort((a, b) => weight(a) - weight(b));
}

/** "1 opportunity, 1 industry story" — the shape of the day in a few words. */
function shapeOf(items: NewsItem[]): string {
  const opportunities = items.filter(item => item.category === 'opportunity').length;
  const industry = items.length - opportunities;
  const parts: string[] = [];
  if (opportunities > 0) parts.push(`${opportunities} opportunit${opportunities === 1 ? 'y' : 'ies'}`);
  if (industry > 0) parts.push(`${industry} industr${industry === 1 ? 'y story' : 'y stories'}`);
  return parts.join(', ');
}
function Story({
  item,
  index,
  lead
}: {
  item: NewsItem;
  index: number;
  lead: boolean;
}) {
  const meta = [item.sourceName, item.publishedOn ? shortDate(item.publishedOn) : '', item.deadline ? `Closes ${shortDate(item.deadline)}` : ''].filter(Boolean);
  return <li className="border-t border-ruleDark pb-16 pt-9" data-id="element-1121" data-mp="39r6cq40fgwxf">
      <p className="label-sm m-0 text-paper/35" data-id="element-1122" data-mp="3i98c7fj5913g">
        {String(index).padStart(2, '0')}
        <span className="px-2.5 text-paper/20" data-id="element-1123" data-mp="3malifqq5f6aw">/</span>
        {item.category === 'opportunity' ? 'Opportunity' : 'Industry'}
      </p>

      {/* Full width. This is the line that has to be readable from across the
          desk, and the only thing limiting it should be the page. */}
      <a href={item.url} target="_blank" rel="noopener noreferrer" className="group mt-5 block" data-id="element-1124" data-mp="3dbqdehyuc296">
        <h4 className={`m-0 font-display leading-[1.08] tracking-tight text-paper transition-opacity duration-150 ease-out group-hover:opacity-70 ${lead ? 'text-[1.75rem] sm:text-[2.25rem]' : 'text-2xl sm:text-3xl'}`} data-id="element-1125" data-mp="4w6lbm9wopvp">
          {item.title}
          <ArrowUpRightIcon className="ml-3 inline h-5 w-5 -translate-y-1.5 text-paper/25" strokeWidth={1.25} data-id="element-1126" data-mp="1iz5u2dk7ka" />
        </h4>
      </a>

      <div className="mt-8 grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-12" data-id="element-1127" data-mp="skpue40s56ou">
        <div className="lg:col-span-7" data-id="element-1128" data-mp="303r7ffx4qwmd">
          {item.summary && <p className="m-0 font-sans text-[0.9375rem] leading-[1.8] tracking-wide2 text-paper/60" data-id="element-1129" data-mp="300wo5rpurhuj">
              {item.summary}
            </p>}
          {meta.length > 0 && <p className="label-sm mt-6 text-paper/35" data-id="element-1130" data-mp="2r2t83axmzda4">{meta.join(' · ')}</p>}
        </div>

        {/* Portal's read, in its own column. An opinion should not look like
            more of the article, and a column apart says that better than a
            rule and a gap did. */}
        {item.why && <div className="border-l border-ruleDark pl-6 lg:col-span-5 lg:col-start-8 lg:pl-8" data-id="element-1131" data-mp="3eu8g9kc3gota">
            <p className="label-sm m-0 text-paper/35" data-id="element-1132" data-mp="39zccjmxhwd0c">Why it matters to Portal</p>
            <p className="m-0 mt-4 font-sans text-[0.9375rem] leading-[1.8] tracking-wide2 text-paper/60" data-id="element-1133" data-mp="351syan0ss08e">
              {item.why}
            </p>
          </div>}
      </div>
    </li>;
}
interface Props {
  day: Day;
  /** Today's round-up. Earlier days are pitched down a notch. */
  lead: boolean;
}
export function NewsDay({
  day,
  lead
}: Props) {
  const items = ordered(day.items);
  const label = digestDateLabel(day.date);
  const full = new Date(`${day.date}T00:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const shape = shapeOf(items);
  return <article data-id="element-1134" data-mp="nry5ssm8uwht">
      <p className="label m-0 text-paper/40" data-id="element-1135" data-mp="rvm9h07r97xs">
        {label}
        {label !== full && <span className="text-paper/20" data-id="element-1136" data-mp="rtjmyq5t9wxt"> · {full}</span>}
        {shape && <span className="text-paper/20" data-id="element-1137" data-mp="wted2rhriiht"> · {shape}</span>}
      </p>

      {day.quiet || items.length === 0 ? (
    /* A quiet day is a real answer, not a gap. Said at proper size, so it
       reads as a decision rather than an outage. */
    <p className={`mt-8 max-w-[30ch] font-display leading-[1.1] text-paper/50 ${lead ? 'text-3xl sm:text-4xl' : 'text-2xl'}`} data-id="element-1138" data-mp="1bjlowod9l39j">
          {day.note || 'Nothing worth your time today.'}
        </p>) : <ol className="m-0 mt-10 list-none p-0" data-id="element-1139" data-mp="3cmcq23gzuhnm">
          {items.map((item, position) => <Story key={item.id} item={item} index={position + 1} lead={lead} data-id="element-1140" data-mp="37o1dvm387v28" data-mpi={position} />)}
        </ol>}
    </article>;
}